/**
 * De selectie in de woningtabel: welke rijen aangevinkt staan, en wat het
 * kopvinkje moet doen. Pure rekenkunde zonder React, zodat het te testen is.
 *
 * De selectie leeft in de componentstaat, niet in de URL: wat je aanvinkt is
 * een handeling van nu, geen weergave die je wilt delen.
 */

export type Selectie = ReadonlySet<string>;

export function wissel(selectie: Selectie, id: string): Set<string> {
  const nieuw = new Set(selectie);
  if (nieuw.has(id)) nieuw.delete(id);
  else nieuw.add(id);
  return nieuw;
}

/**
 * Het kopvinkje: staat alles in beeld aan, dan gaat alles in beeld uit; anders
 * gaat alles in beeld aan. Wat buiten beeld al aangevinkt was blijft staan - een
 * "Meer laden" erna mag een eerdere keuze niet wissen.
 */
export function wisselAlles(selectie: Selectie, inBeeld: readonly string[]): Set<string> {
  const nieuw = new Set(selectie);
  if (allesAan(selectie, inBeeld)) {
    for (const id of inBeeld) nieuw.delete(id);
  } else {
    for (const id of inBeeld) nieuw.add(id);
  }
  return nieuw;
}

export function allesAan(selectie: Selectie, inBeeld: readonly string[]): boolean {
  return inBeeld.length > 0 && inBeeld.every((id) => selectie.has(id));
}

/** Sommige maar niet alle rijen in beeld: het kopvinkje staat dan "half". */
export function deelsAan(selectie: Selectie, inBeeld: readonly string[]): boolean {
  return !allesAan(selectie, inBeeld) && inBeeld.some((id) => selectie.has(id));
}

/**
 * Na een nieuwe lading rijen: wat niet meer in beeld is - na een filterwissel
 * of een verwijdering - gaat uit de selectie. Een id dat je niet meer ziet kun
 * je ook niet bewust kiezen.
 */
export function snoei(selectie: Selectie, inBeeld: readonly string[]): Set<string> {
  const beeld = new Set(inBeeld);
  const nieuw = new Set<string>();
  for (const id of selectie) if (beeld.has(id)) nieuw.add(id);
  return nieuw;
}

/** `1 woning` / `12 woningen`, voor in de actiebalk en de bevestiging. */
export function woningenTekst(aantal: number): string {
  return aantal === 1 ? '1 woning' : `${aantal.toLocaleString('nl-NL')} woningen`;
}
